/**
 * Shared Dex-count selector (b15b U2).
 *
 * The Pokédex menu (client/src/pages/Pokedex.tsx) and the Home/Profile KPI
 * strip (client/src/pages/Profile.tsx) used to read their progress numbers
 * from two different places:
 *
 *   - Pokédex menu  → dexStateStore (localStorage, the device-authoritative
 *                     per-variant state the user actually toggles/scans)
 *   - Profile strip → /api/dex/completion + /api/dex/batches.stats (server
 *                     read model, which can lag the local store)
 *
 * That divergence let the two surfaces show different "Main 412 / 410"
 * numbers for the same dex. This selector is the SINGLE SOURCE OF TRUTH:
 * both surfaces now derive their four headline counts from the Pokédex
 * menu's source (`composedVariantSummary`). Profile imports `deriveDexCounts`
 * so its KPI strip can never disagree with the Dex menu again.
 *
 * Kept pure (a plain map over `composedVariantSummary`) so it can be
 * unit-tested with the same fixture both surfaces share — see
 * dexCounts.test.ts, which asserts equality across that one fixture.
 */
import { composedVariantSummary } from "./dexStateStore";

/**
 * The four headline variants the Profile KPI strip surfaces, plus the two size
 * dexes used by the Compare page's expanded head-to-head. Profile and the
 * Friends list read only the headline four; adding fields is additive and
 * leaves those callers untouched.
 */
export interface DexHeadlineCounts {
  /** Main dex — cascade-composed (a Shiny/Lucky/XXL/XXS/Perfect catch counts). */
  main: number;
  /** Shiny variant catches. */
  shiny: number;
  /** Lucky variant catches. */
  lucky: number;
  /** Perfect (100% IV) variant catches. */
  perfect: number;
  /** XXL size-dex catches. */
  xxl: number;
  /** XXS size-dex catches. */
  xxs: number;
  /** Shadow dex catches. */
  shadow: number;
  /** Purified dex catches. */
  purified: number;
  /** Mega dex catches (includes the X/Y secondary form cells). */
  mega: number;
  /** G-Max dex catches. */
  gmax: number;
  /** Shundo (Shiny + 100% IV) — a manual-only dex in its own right. */
  shundo: number;
  /** Shiny Lucky — a manual-only dex in its own right. */
  shinyLucky: number;
}

/**
 * Derive the four Profile/Dex-menu headline counts from the device-local
 * dex state — exactly the numbers the Pokédex menu shows for the
 * All / Shiny / Lucky / Perfect tabs.
 *
 * `main` routes through `composedVariantSummary("pokemon")`, which applies the
 * cascade (a species owned only as Shiny/Lucky/XXL/XXS/Perfect still counts as
 * a Main catch), matching the Pokédex "All" tab headline. The variant counts
 * stay isolated to their own variant, matching the Pokédex Shiny/Lucky/Perfect
 * tab headlines.
 */
export function deriveDexCounts(): DexHeadlineCounts {
  return {
    main: composedVariantSummary("pokemon").present,
    shiny: composedVariantSummary("shiny").present,
    lucky: composedVariantSummary("lucky").present,
    perfect: composedVariantSummary("perfect").present,
    // The remaining dexes, paired with the friend summary columns added in
    // migration 0038. `mega` routes through composedVariantSummary, which adds
    // the X/Y secondary form cells the base row count excludes.
    xxl: composedVariantSummary("xxl").present,
    xxs: composedVariantSummary("xxs").present,
    shadow: composedVariantSummary("shadow").present,
    purified: composedVariantSummary("purified").present,
    mega: composedVariantSummary("mega").present,
    gmax: composedVariantSummary("gmax").present,
    shundo: composedVariantSummary("shundo").present,
    shinyLucky: composedVariantSummary("shinyLucky").present,
  };
}
